import { defineStore } from 'pinia'
import { getConnectedUser, uploadUserAvatar } from '@/api/user'
import { getOngoingCall } from '@/api/call'
import { canProcessLead, canCallLead, canSearchLead } from '@/utils/user'
import { isInboundCall } from '@/utils/call'
import Realtime from '@/api/realtime'
import { ROLE_NAMES } from '@/constants/user'
import * as Sentry from '@sentry/vue'

export const useConnectedUserStore = defineStore('connectedUser', {
  state: () => {
    return {
      user: {
        firstName: '',
        lastName: '',
        avatar: '',
        role: '',
        id: '',
        experiencePoints: 0,
        badges: {},
        tentacalls: {},
        aircallId: null,
        viewingLeadId: null,
        processingLeadId: null,
        callingLeadId: null,
        preferences: {
          tour: true,
          badges: true,
          tentacalls: true,
        },
        call: {
          phoneNumber: '',
          direction: '',
          status: '',
          startedAt: '',
          endedAt: '',
          duration: '',
          detectedLeadId: null,
        },
      },
    }
  },
  actions: {
    async fetchUser() {
      Realtime.unsubscribe(`user:${this.user.id}:lead:update`)
      Realtime.unsubscribe(`user:${this.user.id}:ongoing-call:update`)
      Realtime.unsubscribe(`user:${this.user.id}:experience`)
      Realtime.unsubscribe(`tentacall:${this.user.id}`)
      Realtime.unsubscribe(`badge:${this.user.id}`)

      this.user = await getConnectedUser()

      const { call } = await getOngoingCall(this.user.id)
      this.setCall(call || null)

      Realtime.subscribe(`user:${this.user.id}:experience`, this.setExperiencePoints)
      Realtime.subscribe(`user:${this.user.id}:lead:update`, this.setUserLeadIds)
      Realtime.subscribe(`user:${this.user.id}:ongoing-call:update`, ({ call }) => this.setCall(call))
      
      if (this.user.preferences.tentacalls) {
        Realtime.subscribe(`tentacall:${this.user.id}`, this.setTentacall)
      }

      if (this.user.preferences.badges) {
        Realtime.subscribe(`badge:${this.user.id}`, this.setBadge)
      }

      // Set Sentry user value for easier debugging
      Sentry.setUser({
        id: this.user.id,
        email: this.user.email,
        username: `${this.user.firstName} ${this.user.lastName}`,
      })
    },

    setCall(call) {
      this.user.call = call
    },

    setCallingLead(leadId) {
      this.user.callingLeadId = leadId
    },

    setExperiencePoints(experiencePoints) {
      this.user.experiencePoints = experiencePoints
    },
    setBadge(badge) {
      this.user.gamification.badges[badge] = Date.now()
    },
    setTentacall(tentacall) {
      this.user.gamification.tentacalls[tentacall] =
        this.user.gamification.tentacalls[tentacall] ?
          this.user.gamification.tentacalls[tentacall] + 1 :
          1;
    },
    setUserLeadIds({ processingLeadId, callingLeadId, viewingLeadId }) {
      this.user.processingLeadId = processingLeadId
      this.user.callingLeadId = callingLeadId
      this.user.viewingLeadId = viewingLeadId
    },
    async setAvatar(file) {
      const user = await uploadUserAvatar(file)
      this.user.avatar = user.avatar

      return user.avatar
    },
  },
  getters: {
    isAdmin: (state) => state.user?.role === 'administrator',
    avatar: (state) => state.user?.avatar,
    experiencePoints: (state) => state.user?.experiencePoints,
    firstName: (state) => state.user?.firstName,
    lastName: (state) => state.user?.lastName,
    fullName: (state) => `${state.user?.firstName} ${state.user?.lastName}`,
    role: (state) => state.user?.role,
    roleName: (state) => ROLE_NAMES[state.user?.role],
    id: (state) => state.user?.id,
    aircallId: (state) => state.user?.aircallId,
    processingLeadId: (state) => state.user?.processingLeadId,
    isInitialized: (state) => !!state.user?.id,
    canSearchLead: (state) => canSearchLead(state.user) && !state.hasOngoingCall,
    canProcessLead: (state) => canProcessLead(state.user),
    canCallLead: (state) => canCallLead(state.user),
    callingLeadId: (state) => state.user?.callingLeadId,
    hasCallingLead: (state) => state.user?.callingLeadId !== null,
    hasProcessingLead: (state) => state.user?.processingLeadId !== null,
    experiencePoints: (state) => state.user?.experiencePoints,
    badges: (state) => state.user?.gamification?.badges,
    tentacalls: (state) => state.user?.gamification?.tentacalls,
    call: (state) => state.user?.call,
    preferences: (state) => state.user?.preferences || {},
    hasOngoingCall: (state) => state.user?.call && state.user?.call?.status !== 'done',
    hasOngoingInboundCall: (state) => state.hasCallingLead && state.user?.call && isInboundCall(state.user?.call),
  },
})
